/**
 * CoachPlaybook - StepActionsPanel Component
 * Version: 0.5.145
 * Description: Left-side panel for managing play actions by step
 * 
 * v0.5.75: Removed screen size controls (now standardized)
 */

import React, { useState, useMemo, useEffect } from 'react';

// Speed presets for quick selection
const SPEED_PRESETS = [
  { value: 0.5, label: 'Slow', icon: '🐢' },
  { value: 1, label: 'Normal', icon: '🏃' },
  { value: 1.5, label: 'Fast', icon: '⚡' },
  { value: 2, label: 'Sprint', icon: '🚀' }
];

// Path type icons
const PATH_TYPE_ICONS = {
  movement: '🏃',
  dribble: '⛹️',
  cut: '✂️',
  screen: '🛡️'
};

const PASS_TYPE_ICONS = {
  chest: '➡️',
  bounce: '⤵️',
  overhead: '⬆️',
  skip: '↗️',
  lob: '🌈',
  direct: '»'
};

const StepActionsPanel = ({
  currentPlay,
  currentStep,
  totalSteps,
  animationProgress = 0,
  isExpanded,
  onExpandToggle,
  selectedActionId,
  onActionSelect,
  onActionUpdate,
  onActionDelete,
  onStepChange,
  onJumpToStep,
  onRenumberSteps,
  // Player/Defender props for integrated panel
  positions = [],
  defenders = [],
  onPlayerUpdate,
  onDefenderUpdate,
  onSpotlightChange
}) => {
  const [expandedSteps, setExpandedSteps] = useState(new Set([currentStep]));
  const [editingAction, setEditingAction] = useState(null);
  const [activeTab, setActiveTab] = useState('steps'); // 'steps' | 'active' | 'all'
  
  // Get positions from currentPlay if not passed directly
  const allPositions = positions.length > 0 ? positions : (currentPlay?.positions || []);
  const allDefenders = defenders.length > 0 ? defenders : (currentPlay?.defenders || []);
  
  // Organize all actions by step
  const actionsByStep = useMemo(() => {
    const steps = {};
    
    // Initialize steps 1 through totalSteps
    for (let i = 1; i <= Math.max(totalSteps, 1); i++) {
      steps[i] = {
        offense: [],
        defense: []
      };
    }
    
    // Add paths (movement, dribble, cut)
    (currentPlay?.paths || []).forEach(path => {
      const step = path.stepNumber || 1;
      if (!steps[step]) steps[step] = { offense: [], defense: [] };
      
      const position = allPositions.find(p => p.id === path.positionId);
      steps[step].offense.push({
        id: path.id,
        type: 'path',
        pathType: path.type || 'movement',
        playerId: path.positionId,
        playerLabel: position?.positionLabel || '?',
        playerName: position?.playerName,
        playerColor: position?.color || '#3b82f6',
        speed: path.speed || 1,
        stepNumber: step,
        data: path
      });
    });
    
    // Add passes
    (currentPlay?.passes || []).forEach(pass => {
      const step = pass.stepNumber || 1;
      if (!steps[step]) steps[step] = { offense: [], defense: [] };
      
      const fromPos = allPositions.find(p => p.id === pass.fromPositionId);
      const toPos = allPositions.find(p => p.id === pass.toPositionId);
      steps[step].offense.push({
        id: pass.id,
        type: 'pass',
        passType: pass.passType || 'direct',
        playerId: pass.fromPositionId,
        playerLabel: fromPos?.positionLabel || '?',
        playerName: fromPos?.playerName,
        playerColor: fromPos?.color || '#3b82f6',
        toPlayerId: pass.toPositionId,
        toPlayerLabel: toPos?.positionLabel || '?',
        toPlayerName: toPos?.playerName,
        stepNumber: step,
        data: pass
      });
    });
    
    // Add screens
    (currentPlay?.screens || []).forEach(screen => {
      const step = screen.stepNumber || 1;
      if (!steps[step]) steps[step] = { offense: [], defense: [] };
      
      const setter = allPositions.find(p => p.id === screen.setterId);
      steps[step].offense.push({
        id: screen.id,
        type: 'screen',
        playerId: screen.setterId,
        playerLabel: setter?.positionLabel || '?',
        playerName: setter?.playerName,
        playerColor: setter?.color || '#3b82f6',
        screenName: screen.name,
        rotation: screen.rotation || 0,
        stepNumber: step,
        data: screen
      });
    });
    
    // Add shots
    (currentPlay?.shots || []).forEach(shot => {
      const step = shot.stepNumber || 1;
      if (!steps[step]) steps[step] = { offense: [], defense: [] };
      
      const shooter = allPositions.find(p => p.id === shot.shooterId);
      steps[step].offense.push({
        id: shot.id,
        type: 'shot',
        playerId: shot.shooterId,
        playerLabel: shooter?.positionLabel || '?',
        playerName: shooter?.playerName,
        playerColor: shooter?.color || '#3b82f6',
        stepNumber: step,
        data: shot
      });
    });
    
    // Add defender paths
    (currentPlay?.defenderPaths || []).forEach(defPath => {
      const step = defPath.stepNumber || 1;
      if (!steps[step]) steps[step] = { offense: [], defense: [] };
      
      const defender = allDefenders.find(d => d.id === defPath.defenderId);
      const defIndex = allDefenders.findIndex(d => d.id === defPath.defenderId);
      const displayIndex = defIndex >= 0 ? defIndex + 1 : '?';
      steps[step].defense.push({
        id: defPath.id,
        type: 'defenderPath',
        defenderId: defPath.defenderId,
        defenderLabel: defender?.label || `D${displayIndex}`,
        defenderIndex: displayIndex,
        guardingId: defender?.guardingId,
        speed: defPath.speed || defender?.speed || 1,
        stepNumber: step,
        data: defPath
      });
    });
    
    // v0.5.38: Add implicit "follow" actions for defenders guarding players
    // These show up even without explicit paths drawn
    allDefenders.forEach((defender, defIdx) => {
      if (!defender.guardingId) return; // Not guarding anyone
      
      const guardedPlayer = allPositions.find(p => p.id === defender.guardingId);
      if (!guardedPlayer) return;
      
      const stopStep = defender.stopAtStep || Infinity;
      const defenderPaths = currentPlay?.defenderPaths || [];
      
      // For each step, check if defender has an explicit path or should follow
      for (let step = 1; step <= Math.max(totalSteps, 1); step++) {
        if (!steps[step]) steps[step] = { offense: [], defense: [] };
        
        // Skip if defender has explicit path for this step
        const hasExplicitPath = defenderPaths.some(dp => 
          dp.defenderId === defender.id && (dp.stepNumber || 1) === step
        );
        if (hasExplicitPath) continue;
        
        // Check if there's an offense action in this step to follow
        const offenseHasAction = steps[step].offense.length > 0;
        
        // Check if guarded player has a path in this step
        const guardedHasPath = (currentPlay?.paths || []).some(p => 
          p.positionId === defender.guardingId && (p.stepNumber || 1) === step
        );
        
        // Only add follow action if there's something happening AND defender hasn't stopped
        if ((offenseHasAction || guardedHasPath) && step <= stopStep) {
          steps[step].defense.push({
            id: `follow-${defender.id}-${step}`,
            type: 'defenderFollow',
            defenderId: defender.id,
            defenderLabel: defender.label || `D${defIdx + 1}`,
            defenderIndex: defIdx + 1,
            guardingId: defender.guardingId,
            guardingLabel: guardedPlayer.positionLabel,
            speed: defender.speed || 1,
            stepNumber: step,
            isImplicit: true, // Mark as auto-generated
            stopAtStep: defender.stopAtStep || null
          });
        } else if (step === stopStep + 1 && defender.stopAtStep) {
          // Add a "stopped" indicator at the step after stopAtStep
          steps[step].defense.push({
            id: `stopped-${defender.id}-${step}`,
            type: 'defenderStopped',
            defenderId: defender.id,
            defenderLabel: defender.label || `D${defIdx + 1}`,
            defenderIndex: defIdx + 1,
            stepNumber: step,
            isImplicit: true
          });
        }
      }
    });
    
    return steps;
  }, [currentPlay, totalSteps, allPositions, allDefenders]);
  
  // Get all actions as flat list for "All Actions" view
  const allActionsList = useMemo(() => {
    const actions = [];
    Object.entries(actionsByStep).forEach(([step, { offense, defense }]) => {
      actions.push(...offense, ...defense);
    });
    return actions.sort((a, b) => a.stepNumber - b.stepNumber);
  }, [actionsByStep]);
  
  // Get current step's participants (players and defenders with actions)
  const currentStepParticipants = useMemo(() => {
    const step = currentStep || 1;
    const stepData = actionsByStep[step] || { offense: [], defense: [] };
    
    // Get unique player IDs from offense actions
    const playerIds = new Set();
    const defenderIds = new Set();
    
    stepData.offense.forEach(action => {
      if (action.playerId) playerIds.add(action.playerId);
      if (action.toPlayerId) playerIds.add(action.toPlayerId);
    });
    
    stepData.defense.forEach(action => {
      if (action.defenderId) defenderIds.add(action.defenderId);
    });
    
    // Get full player/defender objects with their actions
    const players = Array.from(playerIds).map(id => {
      const position = allPositions.find(p => p.id === id);
      const actions = stepData.offense.filter(a => a.playerId === id || a.toPlayerId === id);
      return {
        ...position,
        actions,
        speed: position?.speed || 1
      };
    }).filter(p => p.id);
    
    const defs = Array.from(defenderIds).map(id => {
      const defender = allDefenders.find(d => d.id === id);
      const defIndex = allDefenders.findIndex(d => d.id === id);
      const actions = stepData.defense.filter(a => a.defenderId === id);
      return {
        ...defender,
        index: defIndex + 1,
        actions,
        speed: defender?.speed || 1
      };
    }).filter(d => d.id);
    
    return { players, defenders: defs, offense: stepData.offense, defense: stepData.defense };
  }, [currentStep, actionsByStep, allPositions, allDefenders]);
  
  // Check if steps need renumbering (gaps exist)
  const hasGaps = useMemo(() => {
    const usedSteps = new Set();
    allActionsList.forEach(action => usedSteps.add(action.stepNumber));
    const sortedSteps = Array.from(usedSteps).sort((a, b) => a - b);
    for (let i = 0; i < sortedSteps.length; i++) {
      if (sortedSteps[i] !== i + 1) return true;
    }
    return false;
  }, [allActionsList]);
  
  const toggleStepExpand = (step) => {
    const newExpanded = new Set(expandedSteps);
    if (newExpanded.has(step)) {
      newExpanded.delete(step);
    } else {
      newExpanded.add(step);
    }
    setExpandedSteps(newExpanded);
  };
  
  // Auto-expand current step
  useEffect(() => {
    if (currentStep > 0) {
      setExpandedSteps(prev => new Set([...prev, currentStep]));
    }
  }, [currentStep]);
  
  const handleActionClick = (action, e, fromAllTab = false) => {
    e.stopPropagation();
    onActionSelect?.(action.id, action.type);
    setEditingAction(action.id === editingAction ? null : action.id);
    
    // If clicking from All tab, switch to Steps tab and expand that step
    if (fromAllTab) {
      setActiveTab('steps');
      setExpandedSteps(prev => new Set([...prev, action.stepNumber]));
      // Also jump to that step in the animation
      onJumpToStep?.(action.stepNumber);
    }
    
    // Spotlight the player/defender
    if (action.playerId) {
      onSpotlightChange?.(action.playerId);
    } else if (action.defenderId) {
      // For defender actions, we could spotlight differently if needed
    }
  };
  
  const handleStepReassign = (action, newStep) => {
    if (newStep < 1) newStep = 1;
    onStepChange?.(action.id, action.type, newStep);
    setEditingAction(null);
  };
  
  const handleSpeedChange = (action, newSpeed) => {
    onActionUpdate?.(action.id, action.type, { speed: newSpeed });
  };
  
  const handleRotationChange = (action, newRotation) => {
    // Normalize rotation to 0-360 range
    let normalized = newRotation % 360;
    if (normalized < 0) normalized += 360;
    onActionUpdate?.(action.id, action.type, { rotation: normalized });
  };
  
  const handleScreenSizeChange = (action, dimension, value) => {
    onActionUpdate?.(action.id, action.type, { [dimension]: value });
  };
  
  const handleScreenShapeChange = (action, shape) => {
    onActionUpdate?.(action.id, action.type, { shape });
  };
  
  const handleDeleteAction = (action, e) => {
    e.stopPropagation();
    if (onActionDelete) {
      onActionDelete(action.id, action.type);
    }
  };
  
  const getActionIcon = (action) => {
    if (action.type === 'path') {
      return PATH_TYPE_ICONS[action.pathType] || '🏃';
    }
    if (action.type === 'pass') {
      return PASS_TYPE_ICONS[action.passType] || '➡️';
    }
    if (action.type === 'screen') return '🛡️';
    if (action.type === 'shot') return '🎯';
    if (action.type === 'defenderPath') return '🔴';
    if (action.type === 'defenderFollow') return '👁️';
    if (action.type === 'defenderStopped') return '🛑';
    return '•';
  };
  
  const getActionDescription = (action) => {
    if (action.type === 'path') {
      const typeLabel = action.pathType === 'dribble' ? 'Dribble' : action.pathType === 'cut' ? 'Cut' : 'Move';
      return `${action.playerLabel} ${typeLabel}`;
    }
    if (action.type === 'pass') {
      return `${action.playerLabel} → ${action.toPlayerLabel}`;
    }
    if (action.type === 'screen') {
      return `${action.playerLabel} Screen`;
    }
    if (action.type === 'shot') {
      return `${action.playerLabel} Shot`;
    }
    if (action.type === 'defenderPath') {
      return `D${action.defenderIndex} Move`;
    }
    if (action.type === 'defenderFollow') {
      return `D${action.defenderIndex} → ${action.guardingLabel}`;
    }
    if (action.type === 'defenderStopped') {
      return `D${action.defenderIndex} Stopped`;
    }
    return 'Action';
  };
  
  // Collapsed state
  if (!isExpanded) {
    return (
      <div 
        onClick={onExpandToggle}
        style={{
          width: '40px',
          minHeight: '200px',
          background: 'linear-gradient(145deg, #1e293b, #0f172a)',
          borderRadius: '0 12px 12px 0',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '2px 0 10px rgba(0,0,0,0.3)',
          transition: 'all 0.2s ease',
          flexShrink: 0
        }}
      >
        <span style={{ 
          writingMode: 'vertical-rl', 
          textOrientation: 'mixed',
          color: '#94a3b8',
          fontSize: '12px',
          fontWeight: '600',
          letterSpacing: '1px'
        }}>
          📋 ACTIONS
        </span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" style={{ marginTop: '8px' }}>
          <polyline points="9 18 15 12 9 6"/>
        </svg>
      </div>
    );
  }
  
  return (
    <div style={{
      width: '300px',
      minWidth: '300px',
      background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
      borderRadius: '0 16px 16px 0',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '4px 0 20px rgba(0,0,0,0.3)',
      overflow: 'hidden',
      flexShrink: 0
    }}>
      {/* Header with Tabs */}
      <div style={{
        padding: '12px 12px 0',
        borderBottom: '1px solid rgba(148, 163, 184, 0.1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '16px' }}>📋</span>
            <span style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '14px' }}>Play Actions</span>
            <span style={{ 
              background: '#3b82f6', 
              color: '#fff', 
              padding: '2px 6px', 
              borderRadius: '8px',
              fontSize: '10px',
              fontWeight: '700'
            }}>
              {allActionsList.length}
            </span>
          </div>
          <button
            onClick={onExpandToggle}
            style={{
              background: 'rgba(148, 163, 184, 0.1)',
              border: 'none',
              borderRadius: '6px',
              padding: '4px 8px',
              cursor: 'pointer',
              color: '#94a3b8',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '11px'
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
          </button>
        </div>
        
        {/* Tab Buttons */}
        <div style={{ display: 'flex', gap: '4px' }}>
          {[
            { id: 'active', label: '⚡ Active', desc: 'Current step' },
            { id: 'steps', label: '📊 Steps', desc: 'By step' },
            { id: 'all', label: '📜 All', desc: 'Timeline' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                flex: 1,
                padding: '8px 4px',
                borderRadius: '8px 8px 0 0',
                border: 'none',
                background: activeTab === tab.id 
                  ? 'rgba(59, 130, 246, 0.2)' 
                  : 'transparent',
                borderBottom: activeTab === tab.id 
                  ? '2px solid #3b82f6' 
                  : '2px solid transparent',
                color: activeTab === tab.id ? '#60a5fa' : '#64748b',
                fontSize: '11px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>
      
      {/* Content Area */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
        
        {/* ===== ACTIVE STEP TAB ===== */}
        {activeTab === 'active' && (
          <div>
            {/* Current Step Header */}
            <div style={{
              background: 'linear-gradient(135deg, #1e40af, #1d4ed8)',
              borderRadius: '12px',
              padding: '12px',
              marginBottom: '12px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ color: '#93c5fd', fontSize: '10px', fontWeight: '600', textTransform: 'uppercase' }}>
                    Now Playing
                  </div>
                  <div style={{ color: '#fff', fontSize: '24px', fontWeight: '700' }}>
                    Step {currentStep || 1}
                  </div>
                </div>
                <div style={{ 
                  background: 'rgba(255,255,255,0.2)', 
                  borderRadius: '8px', 
                  padding: '8px 12px',
                  textAlign: 'center'
                }}>
                  <div style={{ color: '#93c5fd', fontSize: '10px' }}>Progress</div>
                  <div style={{ color: '#fff', fontSize: '16px', fontWeight: '700' }}>
                    {Math.round(animationProgress * 100)}%
                  </div>
                </div>
              </div>
              
              {/* Step Navigation */}
              <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                <button
                  onClick={() => onJumpToStep?.(Math.max(1, (currentStep || 1) - 1))}
                  disabled={currentStep <= 1}
                  style={{
                    flex: 1,
                    padding: '8px',
                    borderRadius: '6px',
                    border: 'none',
                    background: currentStep <= 1 ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.2)',
                    color: currentStep <= 1 ? '#64748b' : '#fff',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: currentStep <= 1 ? 'not-allowed' : 'pointer'
                  }}
                >
                  ◀ Prev
                </button>
                <button
                  onClick={() => onJumpToStep?.((currentStep || 1) + 1)}
                  disabled={currentStep >= totalSteps}
                  style={{
                    flex: 1,
                    padding: '8px',
                    borderRadius: '6px',
                    border: 'none',
                    background: currentStep >= totalSteps ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.2)',
                    color: currentStep >= totalSteps ? '#64748b' : '#fff',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: currentStep >= totalSteps ? 'not-allowed' : 'pointer'
                  }}
                >
                  Next ▶
                </button>
              </div>
            </div>
            
            {/* Participants in Current Step */}
            {currentStepParticipants.players.length === 0 && currentStepParticipants.defenders.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '24px',
                color: '#64748b'
              }}>
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>🏀</div>
                <div style={{ fontSize: '13px' }}>No actions in Step {currentStep || 1}</div>
              </div>
            ) : (
              <>
                {/* Offense Players */}
                {currentStepParticipants.players.length > 0 && (
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{
                      fontSize: '10px',
                      color: '#3b82f6',
                      fontWeight: '700',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      marginBottom: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <span>⚪</span> Offense ({currentStepParticipants.players.length})
                    </div>
                    
                    {currentStepParticipants.players.map(player => (
                      <PlayerSpeedCard
                        key={player.id}
                        player={player}
                        isOffense={true}
                        onSpeedChange={(speed) => onPlayerUpdate?.(player.id, { speed })}
                        onSpotlight={() => onSpotlightChange?.(player.id)}
                      />
                    ))}
                  </div>
                )}
                
                {/* Defense */}
                {currentStepParticipants.defenders.length > 0 && (
                  <div>
                    <div style={{
                      fontSize: '10px',
                      color: '#dc2626',
                      fontWeight: '700',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      marginBottom: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <span>🔴</span> Defense ({currentStepParticipants.defenders.length})
                    </div>
                    
                    {currentStepParticipants.defenders.map(defender => (
                      <DefenderSpeedCard
                        key={defender.id}
                        defender={defender}
                        onSpeedChange={(speed) => onDefenderUpdate?.(defender.id, { speed })}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
        
        {/* ===== STEPS TAB ===== */}
        {activeTab === 'steps' && (
          <div>
            {/* Renumber Button */}
            {hasGaps && (
              <button
                onClick={() => onRenumberSteps?.()}
                style={{
                  width: '100%',
                  padding: '10px',
                  marginBottom: '8px',
                  background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                🔄 Renumber Steps (fix gaps)
              </button>
            )}
            
            {totalSteps === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '32px 16px',
                color: '#64748b'
              }}>
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>📝</div>
                <div style={{ fontSize: '13px' }}>No actions yet</div>
                <div style={{ fontSize: '11px', marginTop: '4px' }}>Draw paths or passes to add actions</div>
              </div>
            ) : (
              Object.entries(actionsByStep).map(([stepNum, { offense, defense }]) => {
                const step = parseInt(stepNum);
                const isCurrentStep = currentStep === step;
                const isPastStep = currentStep > step;
                const isStepExpanded = expandedSteps.has(step);
                const totalActions = offense.length + defense.length;
                
                if (totalActions === 0) return null;
                
                return (
                  <StepGroup
                    key={step}
                    step={step}
                    isCurrentStep={isCurrentStep}
                    isPastStep={isPastStep}
                    isExpanded={isStepExpanded}
                    offense={offense}
                    defense={defense}
                    totalSteps={totalSteps}
                    selectedActionId={selectedActionId}
                    editingAction={editingAction}
                    onToggle={() => toggleStepExpand(step)}
                    onJumpToStep={onJumpToStep}
                    onActionClick={handleActionClick}
                    onStepReassign={handleStepReassign}
                    onSpeedChange={handleSpeedChange}
                    onRotationChange={handleRotationChange}
                    onScreenSizeChange={handleScreenSizeChange}
                    onScreenShapeChange={handleScreenShapeChange}
                    onDeleteAction={handleDeleteAction}
                    getActionIcon={getActionIcon}
                    getActionDescription={getActionDescription}
                  />
                );
              })
            )}
          </div>
        )}
        
        {/* ===== ALL ACTIONS TAB ===== */}
        {activeTab === 'all' && (
          <div>
            {allActionsList.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '32px 16px',
                color: '#64748b'
              }}>
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>📜</div>
                <div style={{ fontSize: '13px' }}>No actions recorded</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {allActionsList.map((action, idx) => (
                  <div
                    key={action.id}
                    onClick={(e) => handleActionClick(action, e, true)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '10px 12px',
                      background: selectedActionId === action.id 
                        ? action.type === 'defenderPath' ? 'rgba(220, 38, 38, 0.3)' : 'rgba(59, 130, 246, 0.3)'
                        : 'rgba(51, 65, 85, 0.5)',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      border: selectedActionId === action.id
                        ? `2px solid ${action.type === 'defenderPath' ? '#dc2626' : '#3b82f6'}`
                        : '1px solid rgba(148, 163, 184, 0.1)',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    {/* Step Badge */}
                    <div style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '6px',
                      background: action.type === 'defenderPath' ? '#dc2626' : '#3b82f6',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: '700',
                      flexShrink: 0
                    }}>
                      {action.stepNumber}
                    </div>
                    
                    {/* Icon */}
                    <span style={{ fontSize: '16px', flexShrink: 0 }}>{getActionIcon(action)}</span>
                    
                    {/* Description */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: '#f1f5f9', fontSize: '12px', fontWeight: '600' }}>
                        {getActionDescription(action)}
                      </div>
                      {action.playerName && (
                        <div style={{ color: '#64748b', fontSize: '10px' }}>{action.playerName}</div>
                      )}
                    </div>
                    
                    {/* Speed Badge */}
                    {(action.type === 'path' || action.type === 'defenderPath') && (
                      <div style={{
                        background: 'rgba(0,0,0,0.3)',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontSize: '10px',
                        color: '#94a3b8',
                        fontWeight: '600'
                      }}>
                        {action.speed}x
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Footer */}
      <div style={{
        padding: '10px 12px',
        borderTop: '1px solid rgba(148, 163, 184, 0.1)',
        background: 'rgba(15, 23, 42, 0.5)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '10px', color: '#64748b' }}>
            💡 Click action to edit
          </span>
          <span style={{ fontSize: '10px', color: '#64748b' }}>
            {totalSteps} steps
          </span>
        </div>
      </div>
    </div>
  );
};

// Step Group Component
const StepGroup = ({
  step,
  isCurrentStep,
  isPastStep,
  isExpanded,
  offense,
  defense,
  totalSteps,
  selectedActionId,
  editingAction,
  onToggle,
  onJumpToStep,
  onActionClick,
  onStepReassign,
  onSpeedChange,
  onRotationChange,
  onScreenSizeChange,
  onScreenShapeChange,
  onDeleteAction,
  getActionIcon,
  getActionDescription
}) => {
  const totalActions = offense.length + defense.length;
  
  return (
    <div style={{
      marginBottom: '8px',
      borderRadius: '10px',
      overflow: 'hidden',
      border: isCurrentStep 
        ? '2px solid #3b82f6' 
        : '1px solid rgba(148, 163, 184, 0.15)',
      background: isCurrentStep 
        ? 'rgba(59, 130, 246, 0.1)' 
        : isPastStep 
          ? 'rgba(34, 197, 94, 0.05)' 
          : 'rgba(30, 41, 59, 0.5)'
    }}>
      {/* Step Header */}
      <div 
        onClick={onToggle}
        style={{
          padding: '10px 12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          background: isCurrentStep 
            ? 'linear-gradient(90deg, rgba(59, 130, 246, 0.2), transparent)'
            : 'transparent'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{
            width: '28px',
            height: '28px',
            borderRadius: '6px',
            background: isCurrentStep 
              ? '#3b82f6' 
              : isPastStep 
                ? '#22c55e' 
                : '#475569',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '13px',
            fontWeight: '700'
          }}>
            {step}
          </span>
          <span style={{
            color: isCurrentStep ? '#60a5fa' : '#94a3b8',
            fontSize: '13px',
            fontWeight: '600'
          }}>
            Step {step}
          </span>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {offense.length > 0 && (
            <span style={{
              background: '#3b82f6',
              color: '#fff',
              padding: '2px 8px',
              borderRadius: '10px',
              fontSize: '11px',
              fontWeight: '600'
            }}>
              {offense.length}⚪
            </span>
          )}
          {defense.length > 0 && (
            <span style={{
              background: '#dc2626',
              color: '#fff',
              padding: '2px 8px',
              borderRadius: '10px',
              fontSize: '11px',
              fontWeight: '600'
            }}>
              {defense.length}🔴
            </span>
          )}
          <svg 
            width="14" height="14" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="#64748b" 
            strokeWidth="2"
            style={{
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease'
            }}
          >
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </div>
      </div>
      
      {/* Step Actions */}
      {isExpanded && (
        <div style={{ padding: '0 8px 8px' }}>
          {/* Jump to Step Button */}
          <button
            onClick={(e) => { e.stopPropagation(); onJumpToStep?.(step); }}
            style={{
              width: '100%',
              padding: '6px',
              marginBottom: '8px',
              background: 'rgba(59, 130, 246, 0.2)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              borderRadius: '6px',
              color: '#60a5fa',
              fontSize: '11px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px'
            }}
          >
            ▶️ Jump to Step {step}
          </button>
          
          {/* Offense Actions */}
          {offense.length > 0 && (
            <div style={{ marginBottom: defense.length > 0 ? '8px' : 0 }}>
              <div style={{
                fontSize: '9px',
                color: '#3b82f6',
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '4px',
                paddingLeft: '4px'
              }}>
                Offense
              </div>
              {offense.map(action => (
                <ActionItem
                  key={action.id}
                  action={action}
                  isSelected={selectedActionId === action.id}
                  isEditing={editingAction === action.id}
                  onClick={onActionClick}
                  onStepChange={onStepReassign}
                  onSpeedChange={onSpeedChange}
                  onRotationChange={onRotationChange}
                  onScreenSizeChange={onScreenSizeChange}
                  onScreenShapeChange={onScreenShapeChange}
                  onDelete={onDeleteAction}
                  totalSteps={totalSteps}
                  getIcon={getActionIcon}
                  getDescription={getActionDescription}
                  isOffense={true}
                />
              ))}
            </div>
          )}
          
          {/* Defense Actions */}
          {defense.length > 0 && (
            <div>
              <div style={{
                fontSize: '9px',
                color: '#dc2626',
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '4px',
                paddingLeft: '4px'
              }}>
                Defense
              </div>
              {defense.map(action => (
                <ActionItem
                  key={action.id}
                  action={action}
                  isSelected={selectedActionId === action.id}
                  isEditing={editingAction === action.id}
                  onClick={onActionClick}
                  onStepChange={onStepReassign}
                  onSpeedChange={onSpeedChange}
                  onRotationChange={onRotationChange}
                  onScreenSizeChange={onScreenSizeChange}
                  onScreenShapeChange={onScreenShapeChange}
                  onDelete={onDeleteAction}
                  totalSteps={totalSteps}
                  getIcon={getActionIcon}
                  getDescription={getActionDescription}
                  isOffense={false}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Individual Action Item Component
const ActionItem = ({
  action,
  isSelected,
  isEditing,
  onClick,
  onStepChange,
  onSpeedChange,
  onRotationChange,
  onScreenSizeChange,
  onScreenShapeChange,
  onDelete,
  totalSteps,
  getIcon,
  getDescription,
  isOffense
}) => {
  return (
    <div style={{ marginBottom: '4px' }}>
      <div
        onClick={(e) => onClick(action, e)}
        style={{
          padding: '8px 10px',
          borderRadius: '8px',
          background: isSelected 
            ? isOffense ? 'rgba(59, 130, 246, 0.3)' : 'rgba(220, 38, 38, 0.3)'
            : 'rgba(51, 65, 85, 0.5)',
          border: isSelected 
            ? `2px solid ${isOffense ? '#3b82f6' : '#dc2626'}`
            : '1px solid rgba(148, 163, 184, 0.1)',
          cursor: 'pointer',
          transition: 'all 0.15s ease'
        }}
      >
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          gap: '8px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
            <span style={{ fontSize: '14px', flexShrink: 0 }}>{getIcon(action)}</span>
            <div style={{ minWidth: 0 }}>
              <div style={{
                color: '#f1f5f9',
                fontSize: '12px',
                fontWeight: '600',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                {getDescription(action)}
              </div>
              {action.playerName && (
                <div style={{ color: '#64748b', fontSize: '10px' }}>
                  {action.playerName}
                </div>
              )}
            </div>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
            {/* Speed indicator for paths */}
            {(action.type === 'path' || action.type === 'defenderPath') && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '2px',
                background: 'rgba(0,0,0,0.3)',
                padding: '2px 6px',
                borderRadius: '4px'
              }}>
                <span style={{ fontSize: '10px' }}>
                  {action.speed <= 0.5 ? '🐢' : action.speed >= 1.5 ? '⚡' : '🏃'}
                </span>
                <span style={{ color: '#94a3b8', fontSize: '10px', fontWeight: '600' }}>
                  {action.speed}x
                </span>
              </div>
            )}
            
            {/* Delete button */}
            {isSelected && (
              <button
                onClick={(e) => onDelete(action, e)}
                style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '4px',
                  border: 'none',
                  background: 'rgba(220, 38, 38, 0.3)',
                  color: '#f87171',
                  fontSize: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Delete action"
              >
                🗑️
              </button>
            )}
          </div>
        </div>
      </div>
      
      {/* Expanded Edit Panel */}
      {isSelected && isEditing && (
        <div style={{
          marginTop: '4px',
          padding: '10px',
          background: 'rgba(15, 23, 42, 0.8)',
          borderRadius: '8px',
          border: '1px solid rgba(148, 163, 184, 0.15)'
        }}>
          {/* Step Assignment */}
          <div style={{ marginBottom: '10px' }}>
            <div style={{
              fontSize: '10px',
              color: '#94a3b8',
              fontWeight: '600',
              marginBottom: '6px'
            }}>
              MOVE TO STEP
            </div>
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {Array.from({ length: totalSteps + 1 }, (_, i) => i + 1).map(step => (
                <button
                  key={step}
                  onClick={(e) => { e.stopPropagation(); onStepChange(action, step); }}
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '6px',
                    border: step === action.stepNumber 
                      ? `2px solid ${isOffense ? '#3b82f6' : '#dc2626'}`
                      : '1px solid rgba(148, 163, 184, 0.2)',
                    background: step === action.stepNumber 
                      ? isOffense ? '#3b82f6' : '#dc2626'
                      : 'rgba(51, 65, 85, 0.5)',
                    color: '#fff',
                    fontSize: '12px',
                    fontWeight: '700',
                    cursor: 'pointer'
                  }}
                >
                  {step > totalSteps ? '+' : step}
                </button>
              ))}
            </div>
          </div>
          
          {/* Speed Control (for paths only) */}
          {(action.type === 'path' || action.type === 'defenderPath') && (
            <div>
              <div style={{
                fontSize: '10px',
                color: '#94a3b8',
                fontWeight: '600',
                marginBottom: '6px'
              }}>
                SPEED
              </div>
              <div style={{ display: 'flex', gap: '4px', marginBottom: '8px' }}>
                {SPEED_PRESETS.map(preset => (
                  <button
                    key={preset.value}
                    onClick={(e) => { e.stopPropagation(); onSpeedChange(action, preset.value); }}
                    style={{
                      flex: 1,
                      padding: '6px 4px',
                      borderRadius: '6px',
                      border: action.speed === preset.value 
                        ? '2px solid #f59e0b'
                        : '1px solid rgba(148, 163, 184, 0.2)',
                      background: action.speed === preset.value 
                        ? 'rgba(245, 158, 11, 0.2)'
                        : 'rgba(51, 65, 85, 0.5)',
                      color: action.speed === preset.value ? '#f59e0b' : '#94a3b8',
                      fontSize: '10px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '2px'
                    }}
                  >
                    <span>{preset.icon}</span>
                    <span>{preset.label}</span>
                  </button>
                ))}
              </div>
              
              {/* Fine-tune slider */}
              <input
                type="range"
                min="0.25"
                max="2.5"
                step="0.25"
                value={action.speed || 1}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => onSpeedChange(action, parseFloat(e.target.value))}
                style={{
                  width: '100%',
                  height: '6px',
                  borderRadius: '3px',
                  appearance: 'none',
                  background: `linear-gradient(to right, #f59e0b 0%, #f59e0b ${((action.speed - 0.25) / 2.25) * 100}%, #334155 ${((action.speed - 0.25) / 2.25) * 100}%, #334155 100%)`,
                  cursor: 'pointer'
                }}
              />
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '9px',
                color: '#64748b',
                marginTop: '2px'
              }}>
                <span>0.25x</span>
                <span style={{ color: '#f59e0b', fontWeight: '700' }}>{action.speed}x</span>
                <span>2.5x</span>
              </div>
            </div>
          )}
          
          {/* Screen Rotation Control (for screens only) */}
          {action.type === 'screen' && (
            <div>
              <div style={{
                fontSize: '10px',
                color: '#94a3b8',
                fontWeight: '600',
                marginBottom: '6px'
              }}>
                SCREEN ROTATION
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  onClick={(e) => { 
                    e.stopPropagation(); 
                    onRotationChange?.(action, (action.rotation || 0) - 15);
                  }}
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '8px',
                    border: '1px solid rgba(148, 163, 184, 0.2)',
                    background: 'rgba(51, 65, 85, 0.5)',
                    color: '#94a3b8',
                    fontSize: '16px',
                    cursor: 'pointer'
                  }}
                  title="Rotate left 15°"
                >
                  ↺
                </button>
                <div style={{
                  flex: 1,
                  textAlign: 'center',
                  color: '#8b5cf6',
                  fontSize: '14px',
                  fontWeight: '700'
                }}>
                  {action.rotation || 0}°
                </div>
                <button
                  onClick={(e) => { 
                    e.stopPropagation(); 
                    onRotationChange?.(action, (action.rotation || 0) + 15);
                  }}
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '8px',
                    border: '1px solid rgba(148, 163, 184, 0.2)',
                    background: 'rgba(51, 65, 85, 0.5)',
                    color: '#94a3b8',
                    fontSize: '16px',
                    cursor: 'pointer'
                  }}
                  title="Rotate right 15°"
                >
                  ↻
                </button>
              </div>
              {/* Quick rotation presets */}
              <div style={{ display: 'flex', gap: '4px', marginTop: '8px' }}>
                {[0, 45, 90, 135, 180].map(angle => (
                  <button
                    key={angle}
                    onClick={(e) => { e.stopPropagation(); onRotationChange?.(action, angle); }}
                    style={{
                      flex: 1,
                      padding: '4px',
                      borderRadius: '4px',
                      border: action.rotation === angle 
                        ? '2px solid #8b5cf6'
                        : '1px solid rgba(148, 163, 184, 0.2)',
                      background: action.rotation === angle 
                        ? 'rgba(139, 92, 246, 0.2)'
                        : 'rgba(51, 65, 85, 0.5)',
                      color: action.rotation === angle ? '#8b5cf6' : '#94a3b8',
                      fontSize: '10px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    {angle}°
                  </button>
                ))}
              </div>
              
              {/* v0.5.75: Screen size is now standardized (120% width, 60% height) */}
              
              {/* Screen Shape Toggle */}
              <div style={{ marginTop: '12px' }}>
                <div style={{
                  fontSize: '10px',
                  color: '#94a3b8',
                  fontWeight: '600',
                  marginBottom: '6px'
                }}>
                  SCREEN SHAPE
                </div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button
                    onClick={(e) => { e.stopPropagation(); onScreenShapeChange?.(action, 'rounded'); }}
                    style={{
                      flex: 1,
                      padding: '8px',
                      borderRadius: '6px',
                      border: (action.data?.shape || 'sharp') === 'rounded' 
                        ? '2px solid #8b5cf6'
                        : '1px solid rgba(148, 163, 184, 0.2)',
                      background: (action.data?.shape || 'sharp') === 'rounded' 
                        ? 'rgba(139, 92, 246, 0.2)'
                        : 'rgba(51, 65, 85, 0.5)',
                      color: (action.data?.shape || 'sharp') === 'rounded' ? '#8b5cf6' : '#94a3b8',
                      fontSize: '11px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    ◯ Rounded
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); onScreenShapeChange?.(action, 'sharp'); }}
                    style={{
                      flex: 1,
                      padding: '8px',
                      borderRadius: '6px',
                      border: (action.data?.shape || 'sharp') === 'sharp' 
                        ? '2px solid #8b5cf6'
                        : '1px solid rgba(148, 163, 184, 0.2)',
                      background: (action.data?.shape || 'sharp') === 'sharp' 
                        ? 'rgba(139, 92, 246, 0.2)'
                        : 'rgba(51, 65, 85, 0.5)',
                      color: (action.data?.shape || 'sharp') === 'sharp' ? '#8b5cf6' : '#94a3b8',
                      fontSize: '11px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    ▭ Sharp
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Player Speed Card for Active Step view
const PlayerSpeedCard = ({ player, isOffense, onSpeedChange, onSpotlight }) => {
  const speed = player.speed || 1;
  const speedColor = speed < 0.8 ? '#ef4444' : speed > 1.2 ? '#22c55e' : '#f59e0b';
  
  return (
    <div style={{
      background: 'rgba(51, 65, 85, 0.5)',
      borderRadius: '10px',
      padding: '10px',
      marginBottom: '8px',
      border: '1px solid rgba(148, 163, 184, 0.1)'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
        <div 
          onClick={onSpotlight}
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            background: player.color || '#3b82f6',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
            fontWeight: '700',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
          }}
        >
          {player.positionLabel}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ color: '#f1f5f9', fontSize: '13px', fontWeight: '600' }}>
            {player.playerName || player.positionLabel}
          </div>
          <div style={{ color: '#64748b', fontSize: '11px' }}>
            {player.actions?.length || 0} action(s) this step
          </div>
        </div>
        <div style={{
          padding: '4px 8px',
          borderRadius: '6px',
          background: 'rgba(0,0,0,0.3)',
          color: speedColor,
          fontSize: '12px',
          fontWeight: '700'
        }}>
          {speed.toFixed(2)}x
        </div>
      </div>
      
      {/* Speed Slider */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ fontSize: '12px' }}>🐢</span>
        <input
          type="range"
          min="0.25"
          max="2.0"
          step="0.25"
          value={speed}
          onChange={(e) => onSpeedChange(parseFloat(e.target.value))}
          style={{
            flex: 1,
            height: '6px',
            borderRadius: '3px',
            appearance: 'none',
            background: `linear-gradient(to right, ${speedColor} 0%, ${speedColor} ${((speed - 0.25) / 1.75) * 100}%, #334155 ${((speed - 0.25) / 1.75) * 100}%, #334155 100%)`,
            cursor: 'pointer'
          }}
        />
        <span style={{ fontSize: '12px' }}>🚀</span>
      </div>
    </div>
  );
};

// Defender Speed Card for Active Step view
const DefenderSpeedCard = ({ defender, onSpeedChange }) => {
  const speed = defender.speed || 1;
  const speedColor = speed < 0.8 ? '#ef4444' : speed > 1.2 ? '#22c55e' : '#f59e0b';
  
  return (
    <div style={{
      background: 'rgba(51, 65, 85, 0.5)',
      borderRadius: '10px',
      padding: '10px',
      marginBottom: '8px',
      border: '1px solid rgba(220, 38, 38, 0.3)'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
        <div style={{
          width: '36px',
          height: '36px',
          borderRadius: '50%',
          background: '#dc2626',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '12px',
          fontWeight: '700',
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
        }}>
          D{defender.index}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ color: '#f1f5f9', fontSize: '13px', fontWeight: '600' }}>
            Defender {defender.index}
          </div>
          <div style={{ color: '#64748b', fontSize: '11px' }}>
            {defender.actions?.length || 0} action(s) this step
          </div>
        </div>
        <div style={{
          padding: '4px 8px',
          borderRadius: '6px',
          background: 'rgba(0,0,0,0.3)',
          color: speedColor,
          fontSize: '12px',
          fontWeight: '700'
        }}>
          {speed.toFixed(2)}x
        </div>
      </div>
      
      {/* Speed Slider */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ fontSize: '12px' }}>🐢</span>
        <input
          type="range"
          min="0.25"
          max="2.0"
          step="0.25"
          value={speed}
          onChange={(e) => onSpeedChange(parseFloat(e.target.value))}
          style={{
            flex: 1,
            height: '6px',
            borderRadius: '3px',
            appearance: 'none',
            background: `linear-gradient(to right, ${speedColor} 0%, ${speedColor} ${((speed - 0.25) / 1.75) * 100}%, #334155 ${((speed - 0.25) / 1.75) * 100}%, #334155 100%)`,
            cursor: 'pointer'
          }}
        />
        <span style={{ fontSize: '12px' }}>🚀</span>
      </div>
    </div>
  );
};

export default StepActionsPanel;
